<template>
  <Teleport to="head">
    <component :is="'style'">
      @page { size: A4; margin: 6mm; }
    </component>
  </Teleport>
  <div class="donation-receipt-print">

    <!-- Header: Trust Name LEFT, Receipt badge RIGHT -->
    <div class="header">
      <div class="header-left">
        <div class="trust-name">{{ trustName || businessName }}</div>
        <div v-if="businessAddress" class="trust-detail">{{ businessAddress }}</div>
        <div v-if="trustPan" class="trust-detail">PAN: {{ trustPan }}</div>
      </div>
      <div class="header-right">
        <div class="receipt-type-badge">Donation Receipt</div>
        <div v-if="trust80gRegistration && donation?.donation_type === 'money'" class="g80-badge">80G Approved</div>
      </div>
    </div>

    <!-- Details Bar: Receipt No | Date | FY | Type | Mode -->
    <div class="receipt-bar">
      <div class="bar-group">
        <span class="bar-label">Receipt No.</span>
        <span class="bar-value">{{ donation?.donation_number || donation?.receipt_number || formatReceiptNumber() }}</span>
      </div>
      <div class="bar-group">
        <span class="bar-label">Date</span>
        <span class="bar-value">{{ formatDate(donation?.donation_date) }}</span>
      </div>
      <div class="bar-group">
        <span class="bar-label">Financial Year</span>
        <span class="bar-value">FY {{ financialYear }}</span>
      </div>
      <div class="bar-group">
        <span class="bar-label">Type</span>
        <span class="bar-value">{{ donation?.donation_type ? $t(`donation.types.${donation.donation_type}`) : '' }}</span>
      </div>
      <div v-if="donation?.payment_mode" class="bar-group">
        <span class="bar-label">Mode</span>
        <span class="bar-value">{{ donation.payment_mode }}</span>
      </div>
    </div>

    <!-- 80G Registration (left) + Donor (right) -->
    <div class="parties-section">
      <div class="party-col">
        <div class="party-header">80G Registration</div>
        <template v-if="trust80gRegistration && donation?.donation_type === 'money'">
          <div class="party-detail">Approved u/s 80G(5) of Income Tax Act, 1961</div>
          <div class="party-name">URN: {{ trust80gRegistration }}</div>
          <div v-if="trust80gDin" class="party-detail">DIN: {{ trust80gDin }}</div>
          <div v-if="trust80gValidFrom && trust80gValidTo" class="party-detail">
            Valid: {{ formatDate(trust80gValidFrom) }} to {{ formatDate(trust80gValidTo) }}
          </div>
          <div v-if="trust12aNumber" class="party-detail">12A Reg: {{ trust12aNumber }}</div>
          <div class="party-detail">Eligible for deduction: 50% (with qualifying limit)</div>
        </template>
        <div v-else class="party-detail" style="color: #98a2b3;">Not applicable for this donation type</div>
      </div>
      <div class="party-col">
        <div class="party-header">Donor</div>
        <div class="party-name">{{ patron?.name || 'Anonymous' }}</div>
        <div v-if="patron?.phone" class="party-detail">Ph: {{ patron.phone }}</div>
        <div v-if="patron?.email" class="party-detail">{{ patron.email }}</div>
        <div v-if="patron?.address" class="party-detail">{{ patron.address }}</div>
        <div v-if="patron?.pan" class="party-detail">PAN: {{ patron.pan }}</div>
      </div>
    </div>

    <!-- Donation Details Table -->
    <table class="details-table">
      <thead>
        <tr>
          <th class="col-desc">Description</th>
          <th class="col-amount">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="donation?.description">
          <td>{{ donation.description }}</td>
          <td class="text-right">{{ donation?.donation_type === 'money' ? formatCurrency(donation?.amount) : '-' }}</td>
        </tr>
        <tr v-else>
          <td>
            <template v-if="donation?.donation_type === 'volunteer'">
              Volunteer Service ({{ donation?.volunteer_hours || 0 }} hours)
            </template>
            <template v-else-if="donation?.donation_type === 'in_kind'">
              In-Kind Donation
            </template>
            <template v-else>
              Monetary Donation
            </template>
          </td>
          <td class="text-right">{{ donation?.donation_type === 'money' ? formatCurrency(donation?.amount) : '-' }}</td>
        </tr>
        <!-- Pad to minimum 3 rows for clean layout -->
        <tr class="empty-row"><td></td><td></td></tr>
        <tr class="empty-row"><td></td><td></td></tr>
        <tr class="empty-row"><td></td><td></td></tr>
      </tbody>
      <tfoot>
        <tr>
          <td class="text-right"><strong>Total</strong></td>
          <td class="text-right"><strong>{{ donation?.donation_type === 'money' ? formatCurrency(donation?.amount) : '-' }}</strong></td>
        </tr>
      </tfoot>
    </table>

    <!-- Amount in Words -->
    <div v-if="donation?.donation_type === 'money'" class="words-row">
      <strong>{{ amountInWords }}</strong>
    </div>

    <!-- Footer: Thank you LEFT, Signatory RIGHT -->
    <div class="footer">
      <div class="footer-left">
        <div class="thank-you-text">Thank you for your generous contribution!</div>
        <div v-if="donation?.notes" class="notes-text">Note: {{ donation.notes }}</div>
      </div>
      <div class="signatory-section">
        <span class="sig-for">For {{ trustName || businessName }}</span>
        <div class="sig-space"></div>
        <span class="sig-label">Authorised Signatory</span>
      </div>
    </div>

    <!-- Compliance Notice -->
    <div v-if="trust80gRegistration && donation?.donation_type === 'money'" class="compliance-notice">
      <div>This is a provisional receipt. Form 10BE (Certificate of Donation) will be issued by 31st May after filing Form 10BD with the Income Tax Department.</div>
      <div>Donation eligible for deduction u/s 80G of the Income Tax Act, 1961. Cash donations exceeding Rs. 2,000 are not eligible for deduction u/s 80G(5D).</div>
    </div>

    <!-- Printed On -->
    <div class="printed-on">Printed: {{ formatDateTime(new Date()) }}</div>

  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { amountInWords as toWords } from '@shared/pdf/utils.js'

const { t } = useI18n()

const props = defineProps({
  donation: {
    type: Object,
    required: true,
  },
  patron: {
    type: Object,
    default: () => ({}),
  },
  businessName: {
    type: String,
    default: '',
  },
  businessAddress: {
    type: String,
    default: '',
  },
  trustName: {
    type: String,
    default: '',
  },
  trustPan: {
    type: String,
    default: '',
  },
  trust80gRegistration: {
    type: String,
    default: '',
  },
  trust80gDin: {
    type: String,
    default: '',
  },
  trust80gValidFrom: {
    type: String,
    default: '',
  },
  trust80gValidTo: {
    type: String,
    default: '',
  },
  trust12aNumber: {
    type: String,
    default: '',
  },
})

// Format date
const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY')
}

// Format datetime
const formatDateTime = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY, h:mm A')
}

// Format currency (INR)
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(value)
}

// Generate receipt number if not provided
const formatReceiptNumber = () => {
  const id = props.donation?.id || '000'
  const date = dayjs(props.donation?.donation_date || new Date()).format('YYYYMMDD')
  return `DON-${date}-${String(id).padStart(4, '0')}`
}

// Financial year from donation date (Apr-Mar cycle)
const financialYear = computed(() => {
  const d = dayjs(props.donation?.donation_date || new Date())
  const month = d.month() // 0-indexed
  const year = d.year()
  // FY runs Apr (month 3) to Mar (month 2)
  const startYear = month >= 3 ? year : year - 1
  return `${startYear}-${String(startYear + 1).slice(-2)}`
})

// Amount in words — uses shared Indian-style formatter (Lakh/Crore with Paise)
const amountInWords = computed(() => {
  return toWords(props.donation?.amount || 0)
})
</script>

<style scoped>
/* ─── Container + Design Tokens ──────────────────────────── */
.donation-receipt-print {
  --rcpt-accent: #1a365d;
  --rcpt-text: #1d2939;
  --rcpt-text-muted: #667085;

  width: 210mm;
  min-height: 297mm;
  padding: 8mm;
  margin: 0 auto;
  background: #fff;
  font-family: 'Segoe UI', system-ui, -apple-system, Arial, sans-serif;
  font-size: 9pt;
  color: var(--rcpt-text);
  box-sizing: border-box;
}

/* ─── Header ─────────────────────────────────────────────── */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 10px 5px;
  border: 1px solid #000;
  border-bottom: 2px solid #000;
  margin-bottom: 0;
}

.header-left {
  flex: 1;
}

.trust-name {
  font-size: 14pt;
  font-weight: 700;
  color: var(--rcpt-accent);
  letter-spacing: 0.5px;
  line-height: 1.1;
}

.trust-detail {
  font-size: 7.5pt;
  color: var(--rcpt-text-muted);
  margin-top: 0;
  line-height: 1.3;
}

.header-right {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

.receipt-type-badge {
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--rcpt-accent);
  border: 1.5px solid #000;
  padding: 2px 10px;
}

.g80-badge {
  font-size: 7.5pt;
  font-weight: 600;
  color: var(--rcpt-text);
  border: 1px solid #000;
  padding: 1px 8px;
}

/* ─── Receipt Details Bar ────────────────────────────────── */
.receipt-bar {
  display: flex;
  gap: 0;
  border: 1px solid #000;
  border-top: none;
  margin-bottom: 0;
}

.bar-group {
  flex: 1;
  padding: 3px 8px;
  border-right: 1px solid #98a2b3;
}

.bar-group:last-child {
  border-right: none;
}

.bar-label {
  display: block;
  font-size: 7pt;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rcpt-text-muted);
  font-weight: 600;
}

.bar-value {
  display: block;
  font-size: 8.5pt;
  font-weight: 600;
  color: var(--rcpt-text);
  margin-top: 0;
}

/* ─── Parties: 80G (left) + Donor (right) ────────────────── */
.parties-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #000;
  border-top: none;
  margin-bottom: 0;
}

.party-col {
  padding: 3px 8px;
}

.party-col:first-child {
  border-right: 1px solid #000;
}

.party-header {
  font-size: 6.5pt;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--rcpt-accent);
  font-weight: 700;
  margin-bottom: 1px;
}

.party-name {
  font-size: 8.5pt;
  font-weight: 600;
  color: var(--rcpt-text);
  margin-bottom: 1px;
}

.party-detail {
  font-size: 7.5pt;
  color: var(--rcpt-text-muted);
  line-height: 1.2;
}

/* ─── Donation Details Table ─────────────────────────────── */
.details-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  border: 1px solid #000;
  border-top: none;
}

.details-table thead th {
  font-size: 7pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 3px 4px;
  border: 1px solid #000;
  border-bottom: 2px solid #000;
  color: var(--rcpt-text);
}

.details-table thead th.col-desc {
  text-align: left;
}

.details-table thead th.col-amount {
  text-align: right;
  width: 100px;
}

.details-table tbody td {
  padding: 3px 4px;
  font-size: 8pt;
  border-left: 1px solid #98a2b3;
  border-right: 1px solid #98a2b3;
  border-bottom: 1px solid #d0d5dd;
  vertical-align: middle;
  line-height: 1.2;
}

.empty-row td {
  height: 14px;
  border-left: 1px solid #98a2b3;
  border-right: 1px solid #98a2b3;
  border-bottom: 1px solid #d0d5dd;
}

.details-table tfoot td {
  padding: 3px 4px;
  font-size: 8pt;
  font-weight: 700;
  border: 1px solid #000;
  border-top: 2px solid #000;
}

/* ─── Amount in Words ────────────────────────────────────── */
.words-row {
  padding: 3px 8px;
  font-size: 7.5pt;
  font-style: italic;
  color: var(--rcpt-text);
  border: 1px solid #000;
  border-top: none;
}

/* ─── Footer: Thank you LEFT, Signatory RIGHT ────────────── */
.footer {
  display: flex;
  align-items: stretch;
  border: 1px solid #000;
  border-top: none;
  min-height: 52px;
}

.footer-left {
  flex: 1;
  padding: 4px 8px;
  border-right: 1px solid #000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.thank-you-text {
  font-size: 8pt;
  font-weight: 600;
  color: var(--rcpt-text);
}

.notes-text {
  font-size: 7pt;
  color: var(--rcpt-text-muted);
  font-style: italic;
}

.compliance-notice {
  border: 1px solid #000;
  border-top: none;
  padding: 4px 8px;
  font-size: 6.5pt;
  color: var(--rcpt-text-muted);
  line-height: 1.4;
}

.compliance-notice div + div {
  margin-top: 2px;
}

.printed-on {
  font-size: 6.5pt;
  color: var(--rcpt-text-muted);
  margin-top: 4px;
  text-align: center;
}

.signatory-section {
  flex: 0 0 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 3px 6px;
  text-align: center;
}

.sig-for {
  font-size: 7pt;
  font-weight: 600;
  color: var(--rcpt-text);
}

.sig-space {
  flex: 1;
  min-height: 20px;
}

.sig-label {
  font-size: 6.5pt;
  color: var(--rcpt-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ─── Utility Classes ────────────────────────────────────── */
.text-right { text-align: right; }
.text-center { text-align: center; }

/* ─── Print Media ────────────────────────────────────────── */
@media print {
  .donation-receipt-print {
    width: 100%;
    margin: 0;
    padding: 4mm 6mm;
  }

  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
